import { Vector2, Vector3 } from 'three';
const DEFAULT_FLOAT_EPSILON = 1e-5;
export function areEqual(left, right, epsilon = DEFAULT_FLOAT_EPSILON) {
    return Math.abs(left - right) <= epsilon;
}
export function areNotEqual(left, right, epsilon = DEFAULT_FLOAT_EPSILON) {
    return Math.abs(left - right) > epsilon;
}
export function areVectorsEqual(left, right, epsilon = DEFAULT_FLOAT_EPSILON) {
    let equal = true;
    equal && (equal = areEqual(left.x, right.x, epsilon));
    equal && (equal = areEqual(left.y, right.y, epsilon));
    equal && (equal = areEqual(left.z, right.z, epsilon));
    return equal;
}
export function clamp(min, value, max) {
    return Math.min(Math.max(value, min), max);
}
/**
 *
 * @param seg1Begin begin point of first segment
 * @param seg1End end point of first segment
 * @param seg2Begin begin point of second segment
 * @param seg2End end point of second segment
 * @returns whether first and second segments intersect
 */
export function doSegmentsIntersect(seg1Begin, seg1End, seg2Begin, seg2End) {
    const segment1Dir = seg1End.clone().sub(seg1Begin);
    const segment2Dir = seg2End.clone().sub(seg2Begin);
    const determinant = segment1Dir.x * segment2Dir.y - segment1Dir.y * segment2Dir.x;
    // If the determinant is zero, the line segments are parallel and do not intersect
    if (determinant === 0) {
        return false;
    }
    // Calculate the vector between the start points of the line segments
    const segmentStartDiff = seg2Begin.clone().sub(seg1Begin);
    // Calculate the parameter values for the line segments at which they intersect
    const t = (segmentStartDiff.x * segment2Dir.y - segmentStartDiff.y * segment2Dir.x) / determinant;
    const u = (segmentStartDiff.x * segment1Dir.y - segmentStartDiff.y * segment1Dir.x) / determinant;
    // If the parameter values are within the range of 0 to 1, the line segments intersect
    return t >= 0 && t <= 1 && u >= 0 && u <= 1;
}
/**
 * Loops through array of points and on each step returns a pair of consecutive points with their indices
 * @param polygon points of polygon
 */
export function* getPolygonSegments(polygon) {
    yield [polygon[polygon.length - 1], polygon[0], polygon.length - 1, 0];
    for (let index = 0; index < polygon.length - 1; index += 1) {
        yield [polygon[index], polygon[index + 1], index, index + 1];
    }
}
/**
 *
 * @returns whether `polygon1` and `polygon2` intersect
 */
export function doPolygonsIntersect(polygon1, polygon2) {
    for (const [begin, end] of getPolygonSegments(polygon1)) {
        for (const [secondBegin, secondEnd] of getPolygonSegments(polygon2)) {
            if (doSegmentsIntersect(begin, end, secondBegin, secondEnd)) {
                return true;
            }
        }
    }
    return false;
}
/**
 *
 * @returns whether `point` is inside `polygon`
 */
export function isPointInsidePolygon(point, polygon) {
    const yLevel = point.y;
    let isInside = false;
    for (const [begin, end] of getPolygonSegments(polygon)) {
        const hasIntersection = begin.y > yLevel !== end.y > yLevel;
        if (hasIntersection) {
            const intersectionX = begin.x + ((yLevel - begin.y) * (end.x - begin.x)) / (end.y - begin.y);
            if (intersectionX > point.x) {
                isInside = !isInside;
            }
        }
    }
    return isInside;
}
export function getBox3Vertices(box) {
    const { min, max } = box;
    return [
        new Vector3(max.x, max.y, max.z),
        new Vector3(min.x, max.y, max.z),
        new Vector3(min.x, min.y, max.z),
        new Vector3(max.x, min.y, max.z),
        new Vector3(max.x, max.y, min.z),
        new Vector3(min.x, max.y, min.z),
        new Vector3(min.x, min.y, min.z),
        new Vector3(max.x, min.y, min.z),
    ];
}
/**
 * @param point point in 2D space
 * @param camera camera for projection calculation
 * @param domElement renderer dom element
 * @returns position of given point on a page in pixels
 */
export function project(point, camera, domElement) {
    const screenPosition = new Vector3();
    const rect = domElement.getBoundingClientRect();
    screenPosition.copy(point);
    screenPosition.project(camera);
    const widthHalf = 0.5 * rect.width;
    const heightHalf = 0.5 * rect.height;
    screenPosition.x = screenPosition.x * widthHalf + widthHalf + rect.left;
    screenPosition.y = -(screenPosition.y * heightHalf) + heightHalf + rect.top;
    return new Vector2(screenPosition.x, screenPosition.y);
}
